<!--
    No global loading bar.

    It reported the same fetch as the list skeletons, so every list screen
    showed two unrelated-looking loads at once: a bar at the top of the window
    and skeleton rows further down. The skeletons say "this list is loading"
    in the place the answer will appear, which the bar cannot. Screens with a
    load of their own still render their own affordance.

    Only this layout drops it; the other layouts (empty/dense/…) keep theirs.
-->

<!--
    Navigation

    Light rail, not the Fuse default dark slab.

    `class="dark bg-gray-900"` forced the dark SCHEME on the whole subtree —
    every Fuse colour variable inside the nav swapped, which is why one class
    was enough to paint it. The Easy Khata app has no dark chrome anywhere, so
    the console reading as a dark-sidebar product put the two side by side
    looking like different software.

    Dropping `dark` is safe rather than lucky: the vertical-navigation component
    carries ZERO `dark:` utilities across its twelve template and style files —
    it takes every colour from the theme variables and paints its own surface
    with `background: inherit` (styles/appearances/default.scss). So the host
    class alone decides the surface, and `bg-card` hands it the same white every
    other surface in the admin uses. Identity moves to the primary-coloured
    active item instead of a dark plane.
-->
<fuse-vertical-navigation
    class="ek-nav-rail border-r print:hidden"
    [class.ek-nav-collapsed]="isNavCollapsed && !isScreenSmall"
    [mode]="isScreenSmall ? 'over' : 'side'"
    [name]="'mainNavigation'"
    [navigation]="navigation.default"
    [opened]="!isScreenSmall"
    (click)="onNavClick($event)"
    (mouseover)="onNavHover($event)"
    (mouseleave)="closeFlyout()"
>
    <!--
        Navigation header hook — the LOGO, not the signed-in user.

        This used to be a 96px avatar with the name and email under it, which
        cost ~150px at the top of every screen to answer a question nobody asks
        twice in a session, and left the console opening with a face instead of
        with the product. Identity moved to the header chip beside the sign-out
        control it actually relates to.

        The mark sits on a white plate deliberately: easy-khata-logo.png has
        an OPAQUE white background (its corner pixel is 255,255,255,255), so on
        the dark scheme it would otherwise draw its own hard white rectangle.
        The plate gives that a radius and makes it read as intentional; on the
        light sidebar it is invisible. Swap in a transparent asset and the plate
        can go.
    -->
    <ng-container fuseVerticalNavigationContentHeader>
        <a
            class="ek-brand"
            [routerLink]="['/dashboard']"
            aria-label="Easy Khata — go to dashboard"
        >
            <!--
                Two lockups, one shown at a time. The wordmark needs ~124px and
                the collapsed rail is 76px, so a single scaled image would come
                out either illegible or overflowing. The mark-only SVG is the
                asset that exists for exactly this, and it is transparent — so
                the collapsed state needs no white plate.
            -->
            <span class="ek-brand-plate">
                <img src="assets/img/easy-khata-logo.png" alt="Easy Khata" />
            </span>
            <img
                class="ek-brand-mark"
                src="images/logo/easykhata-logo-mobile.svg"
                alt="Easy Khata"
            />
        </a>
    </ng-container>
    <!-- Navigation footer hook -->
    <!-- <ng-container fuseVerticalNavigationContentFooter>
        <div
            class="mb-4 mt-2 flex h-16 flex-0 items-center justify-center pl-2 pr-6 opacity-12"
        >
            <img class="max-w-36" src="images/logo/logo-text-on-dark.svg" /> -->
    <!-- </div>
    </ng-container>  -->
</fuse-vertical-navigation>

<!--
    The collapsed rail's flyout. A sibling of the nav, not a child: the rail
    clips its own overflow, and a panel that has to sit BESIDE the rail cannot
    live inside it. Fixed to the viewport at the hovered item's own top, so it
    lines up with the icon it belongs to.
-->
@if (flyout && isNavCollapsed && !isScreenSmall) {
    <div
        class="ek-nav-flyout"
        [style.top.px]="flyout.top"
        [style.maxHeight.px]="flyout.maxHeight"
        (mouseenter)="keepFlyout()"
        (mouseleave)="closeFlyout()"
    >
        <div class="ek-nav-flyout-title">{{ flyout.title }}</div>

        <ng-container
            [ngTemplateOutlet]="flyoutItems"
            [ngTemplateOutletContext]="{ $implicit: flyout.children, depth: 0 }"
        ></ng-container>
    </div>
}

<!--
    Recursive, because a group can hold a group: Reports nests its report
    families, and a flat list would either drop them or lie about where they
    live. A branch renders as a heading over its own children, indented one
    step per level; only a leaf — an item with a link and nothing under it —
    is clickable.
-->
<ng-template #flyoutItems let-items let-depth="depth">
    @for (item of items; track item.id) {
        @if (item.children?.length) {
            <div
                class="ek-nav-flyout-group"
                [style.padding-left.px]="10 + depth * 12"
            >
                {{ item.title }}
            </div>
            <ng-container
                [ngTemplateOutlet]="flyoutItems"
                [ngTemplateOutletContext]="{
                    $implicit: item.children,
                    depth: depth + 1
                }"
            ></ng-container>
        } @else {
            <a
                class="ek-nav-flyout-link"
                [style.padding-left.px]="10 + depth * 12"
                [routerLink]="item.link"
                (click)="closeFlyout()"
            >
                @if (item.icon) {
                    <mat-icon class="icon-size-4" [svgIcon]="item.icon"></mat-icon>
                }
                <span>{{ item.title }}</span>
            </a>
        }
    }
</ng-template>

<!-- Wrapper -->
<div class="flex w-full min-w-0 flex-auto flex-col">
    <!-- Header -->
    <div
        class="bg-card relative z-49 flex h-16 w-full flex-0 items-center px-4 shadow dark:border-b dark:bg-transparent dark:shadow-none md:px-6 print:hidden"
    >
        <!-- Navigation toggle button -->
        <button
            mat-icon-button
            (click)="toggleNavigation('mainNavigation')"
            [matTooltip]="
                isScreenSmall
                    ? 'Menu'
                    : isNavCollapsed
                      ? 'Expand navigation'
                      : 'Collapse navigation'
            "
            [attr.aria-label]="
                isScreenSmall
                    ? 'Menu'
                    : isNavCollapsed
                      ? 'Expand navigation'
                      : 'Collapse navigation'
            "
            [attr.aria-expanded]="isScreenSmall ? null : !isNavCollapsed"
        >
            <mat-icon [svgIcon]="'heroicons_outline:bars-3'"></mat-icon>
        </button>
        <!-- Components -->
        <div class="ml-auto flex items-center space-x-0.5 pl-2 sm:space-x-2">
            <!-- <languages></languages> -->
            <!-- <fuse-fullscreen class="hidden md:block"></fuse-fullscreen> -->
            <!-- <search [appearance]="'bar'"></search> -->
            <!-- <shortcuts></shortcuts> -->
            <!-- <messages></messages> -->
            <notifications></notifications>
            <!-- <button
                class="lg:hidden"
                mat-icon-button
                (click)="quickChat.toggle()"
            >
                <mat-icon
                    [svgIcon]="'heroicons_outline:chat-bubble-left-right'"
                ></mat-icon>
            </button> -->

            <!--
                Who is signed in, not just a glyph.

                A bare user-circle was enough while the sidebar spelled out the
                name and email above the nav. That block is now the logo, so
                this is the only thing on screen answering "am I in the right
                account?" — which on a console holding every customer's data is
                worth the few characters.

                Below sm the name and chevron drop and it collapses back to the
                avatar: a phone header has no room to spell identity out.
            -->
            <button
                class="ek-user-chip"
                [matMenuTriggerFor]="userMenu"
                aria-label="Account menu"
            >
                @if (user.imagePath) {
                    <img
                        class="ek-user-chip-avatar"
                        [src]="imageBaseUrl + user.imagePath"
                        alt=""
                    />
                } @else {
                    <mat-icon
                        class="icon-size-8"
                        [svgIcon]="'heroicons_solid:user-circle'"
                    ></mat-icon>
                }
                <span class="ek-user-chip-who hidden sm:flex">
                    <span class="ek-user-chip-name">
                        {{ user.firstName }} {{ user.lastName }}
                    </span>
                    <span class="ek-user-chip-mail">{{ user.email }}</span>
                </span>
                <mat-icon
                    class="ek-user-chip-chev hidden icon-size-4 sm:block"
                    [svgIcon]="'heroicons_mini:chevron-down'"
                ></mat-icon>
            </button>

            <mat-menu
                [xPosition]="'before'"
                #userMenu="matMenu"
                class="user-profile-menu"
            >
                <div class="border-b px-4 py-3">
                    <div class="flex flex-col leading-none">
                        <span class="text-sm">Signed in as</span>
                        <span class="mt-1.5 text-md font-medium">{{
                            user.email
                        }}</span>
                    </div>
                </div>
                <button mat-menu-item [routerLink]="['/profile']">
                    <mat-icon
                        [svgIcon]="'heroicons_outline:user-circle'"
                    ></mat-icon>
                    <span>Profile</span>
                </button>
                <button mat-menu-item (click)="navigateToChangePassword()">
                    <mat-icon [svgIcon]="'heroicons_outline:key'"></mat-icon>
                    <span>Change Password</span>
                </button>
                <mat-divider class="my-2"></mat-divider>
                <button mat-menu-item (click)="logout()">
                    <mat-icon
                        [svgIcon]="'heroicons_outline:arrow-right-on-rectangle'"
                    ></mat-icon>
                    <span>Sign out</span>
                </button>
            </mat-menu>
        </div>
    </div>

    <!-- Content -->
    <div class="flex flex-auto flex-col">
        <!-- *ngIf="true" hack is required here for router-outlet to work correctly.
             Otherwise, layout changes won't be registered and the view won't be updated! -->
        @if (true) {
            <router-outlet></router-outlet>
        }
    </div>

    <!-- Footer -->
    <!--<div class="relative flex flex-0 items-center justify-start w-full h-14 px-4 md:px-6 z-49 border-t bg-card dark:bg-transparent print:hidden">
        <span class="font-medium text-secondary">Fuse &copy; {{currentYear}}</span>
    </div>-->
</div>

<div></div>

<!-- Quick chat -->
<!-- <quick-chat #quickChat="quickChat"></quick-chat> -->

<!-- Change Password Dialog Template -->
<ng-template #changePasswordDialog>
    <div class="min-w-80">
        <div class="flex items-center p-6 pb-4">
            <mat-icon
                class="mr-4 text-primary-500 icon-size-8"
                [svgIcon]="'heroicons_outline:key'"
            ></mat-icon>
            <div>
                <h2 class="text-xl font-semibold">Change Password</h2>
                <p class="text-secondary mt-1">Update your account password</p>
            </div>
        </div>

        <mat-dialog-content class="p-6 pt-0">
            <form [formGroup]="passwordForm">
                <!-- Current Password -->
                <mat-form-field class="mb-4 w-full" appearance="outline">
                    <mat-label>Current Password</mat-label>
                    <input
                        matInput
                        [type]="hideCurrentPassword ? 'password' : 'text'"
                        formControlName="currentPassword"
                        autocomplete="current-password"
                    />
                    <button
                        mat-icon-button
                        matSuffix
                        type="button"
                        (click)="hideCurrentPassword = !hideCurrentPassword"
                        [attr.aria-label]="'Toggle password visibility'"
                    >
                        <mat-icon>{{
                            hideCurrentPassword
                                ? 'visibility_off'
                                : 'visibility'
                        }}</mat-icon>
                    </button>
                    <mat-error
                        *ngIf="
                            passwordForm
                                .get('currentPassword')
                                ?.hasError('required')
                        "
                    >
                        Current password is required
                    </mat-error>
                </mat-form-field>

                <!-- New Password -->
                <mat-form-field class="mb-4 w-full" appearance="outline">
                    <mat-label>New Password</mat-label>
                    <input
                        matInput
                        [type]="hideNewPassword ? 'password' : 'text'"
                        formControlName="newPassword"
                        autocomplete="new-password"
                    />
                    <button
                        mat-icon-button
                        matSuffix
                        type="button"
                        (click)="hideNewPassword = !hideNewPassword"
                        [attr.aria-label]="'Toggle password visibility'"
                    >
                        <mat-icon>{{
                            hideNewPassword ? 'visibility_off' : 'visibility'
                        }}</mat-icon>
                    </button>
                    <mat-error
                        *ngIf="
                            passwordForm
                                .get('newPassword')
                                ?.hasError('required')
                        "
                    >
                        New password is required
                    </mat-error>
                    <mat-error
                        *ngIf="
                            passwordForm
                                .get('newPassword')
                                ?.hasError('minlength')
                        "
                    >
                        Password must be at least 6 characters
                    </mat-error>
                </mat-form-field>

                <!-- Confirm Password -->
                <mat-form-field class="w-full" appearance="outline">
                    <mat-label>Confirm Password</mat-label>
                    <input
                        matInput
                        [type]="hideConfirmPassword ? 'password' : 'text'"
                        formControlName="confirmPassword"
                        autocomplete="new-password"
                    />
                    <button
                        mat-icon-button
                        matSuffix
                        type="button"
                        (click)="hideConfirmPassword = !hideConfirmPassword"
                        [attr.aria-label]="'Toggle password visibility'"
                    >
                        <mat-icon>{{
                            hideConfirmPassword
                                ? 'visibility_off'
                                : 'visibility'
                        }}</mat-icon>
                    </button>
                    <mat-error
                        *ngIf="
                            passwordForm
                                .get('confirmPassword')
                                ?.hasError('required')
                        "
                    >
                        Confirm password is required
                    </mat-error>
                    <mat-error
                        *ngIf="
                            passwordForm
                                .get('confirmPassword')
                                ?.hasError('passwordMismatch')
                        "
                    >
                        Passwords do not match
                    </mat-error>
                </mat-form-field>
            </form>
        </mat-dialog-content>

        <mat-dialog-actions align="end" class="p-6 pt-0">
            <button mat-stroked-button (click)="closePasswordDialog()">
                Cancel
            </button>
            <button
                mat-flat-button
                color="primary"
                [disabled]="passwordForm.invalid || isChangingPassword"
                (click)="changePassword()"
            >
                <mat-progress-spinner
                    *ngIf="isChangingPassword"
                    [diameter]="24"
                    [mode]="'indeterminate'"
                    class="mr-2"
                ></mat-progress-spinner>
                <span>{{
                    isChangingPassword ? 'Changing...' : 'Change Password'
                }}</span>
            </button>
        </mat-dialog-actions>
    </div>
</ng-template>
